import type { BulkSelector, ProposalDecision } from '@fall26/shared'
import { useState } from 'react'
import {
  useBulkApprove,
  useBulkPreview,
  useDecide,
  useIgnored,
  useProposals,
  useRunSync,
  useSyncStatus,
  useUnignore,
} from '../api/hooks.js'
import { Action } from '../components/review/parts.js'
import { ProposalCard } from '../components/review/ProposalCard.js'

/**
 * The approval queue.
 *
 * NOTHING FROM CANVAS TOUCHES MY DATA WITHOUT MY APPROVAL, and this is the
 * screen where the approval happens. A sync run stages proposals; every write
 * to an actual item originates from a tap on this page.
 */
export function Review() {
  const status = useSyncStatus()
  const [showSnoozed, setShowSnoozed] = useState(false)
  const proposals = useProposals(showSnoozed)
  const decide = useDecide()
  const [note, setNote] = useState<string | null>(null)

  const onDecide = (id: number) => (decision: ProposalDecision, description: string) => {
    decide.mutate(
      { id, decision, description },
      {
        onSuccess: (result) =>
          // apiMutate returns null when the mutation went into the queue rather
          // than to the server — during a deploy, most likely. Saying "done"
          // there would be a lie.
          setNote(result === null ? `${description} — queued, will apply when the server is back` : description),
        onError: (error) => setNote((error as Error).message),
      },
    )
  }

  return (
    <section className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-lg font-semibold tracking-tight text-[var(--text)]">Review</h1>
        {status.data && status.data.queue.pending > 0 && (
          <span className="tabular text-[11px] text-[var(--text-muted)]">
            {status.data.queue.pending} waiting
            {status.data.queue.needsIndividualDecision > 0 &&
              ` · ${status.data.queue.needsIndividualDecision} need you`}
          </span>
        )}
      </div>

      <InvalidFeedBanner message={status.data?.invalidFeedMessage ?? null} />
      <SyncBar />

      {note && (
        <p className="tabular rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-sunken)] px-3 py-2 text-[11.5px] text-[var(--text-muted)]">
          {note}
        </p>
      )}

      <BulkBar onNote={setNote} />

      {proposals.isError && (
        <p className="rounded-[var(--radius)] border border-[var(--overdue)]/30 bg-[var(--overdue-wash)] p-3 text-[12.5px] text-[var(--overdue)]">
          Could not load the queue.
        </p>
      )}

      {!proposals.data ? (
        <div className="h-40 animate-pulse rounded-[var(--radius-lg)] bg-[var(--surface-sunken)]" />
      ) : proposals.data.proposals.length === 0 ? (
        <Empty showSnoozed={showSnoozed} skipped={status.data?.queue.skipped ?? 0} />
      ) : (
        <div className="space-y-2.5">
          {proposals.data.proposals.map((proposal) => (
            <ProposalCard
              key={proposal.id}
              proposal={proposal}
              onDecide={onDecide(proposal.id)}
              busy={decide.isPending}
            />
          ))}
        </div>
      )}

      {(status.data?.queue.skipped ?? 0) > 0 && (
        <button
          type="button"
          onClick={() => setShowSnoozed(!showSnoozed)}
          className="tap tabular text-[11px] uppercase tracking-[0.08em] text-[var(--text-subtle)] underline underline-offset-4"
        >
          {showSnoozed ? 'Back to the queue' : `Show ${status.data?.queue.skipped} snoozed`}
        </button>
      )}

      <IgnoreList />
    </section>
  )
}

// ---------------------------------------------------------------------------

/**
 * The banner that has to exist.
 *
 * Canvas regenerates the feed URL whenever it is reset, and the old one then
 * 404s forever. On an hourly cron that failure is completely silent: the queue
 * simply stops filling, which looks exactly like a quiet week. This is the only
 * thing standing between that and finding out in December.
 */
function InvalidFeedBanner({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div
      role="alert"
      className="rounded-[var(--radius)] border border-[var(--overdue)]/40 bg-[var(--overdue-wash)] p-3"
    >
      <div className="tabular text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--overdue)]">
        Canvas feed is dead
      </div>
      <p className="mt-1 text-[12.5px] leading-snug text-[var(--text)]">{message}</p>
    </div>
  )
}

function SyncBar() {
  const status = useSyncStatus()
  const run = useRunSync()
  const [result, setResult] = useState<string | null>(null)

  const display = status.data?.display
  const outcome = status.data?.lastRun?.outcome ?? null
  const failed = outcome !== null && outcome !== 'ok' && outcome !== 'not-modified'

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="tabular text-[10px] uppercase tracking-[0.14em] text-[var(--text-subtle)]">
            Canvas
          </div>
          {status.data && !status.data.feedConfigured ? (
            <p className="mt-0.5 text-[12.5px] text-[var(--text-muted)]">
              No feed configured. Set CANVAS_ICS_URL and restart.
            </p>
          ) : display ? (
            <>
              <p
                className={`tabular mt-0.5 text-[12.5px] ${failed ? 'text-[var(--overdue)]' : 'text-[var(--text)]'}`}
              >
                {display.lastRunSummary}
              </p>
              <p className="tabular mt-0.5 text-[11px] text-[var(--text-subtle)]">
                {display.lastRunRelative} · {display.lastRunAt}
                {failed && ` · last reached Canvas ${display.lastSuccessRelative}`}
              </p>
            </>
          ) : (
            <p className="mt-0.5 text-[12.5px] text-[var(--text-muted)]">Never synced.</p>
          )}
        </div>

        <Action
          busy={run.isPending}
          disabled={status.data ? !status.data.feedConfigured : true}
          onClick={() =>
            run.mutate(undefined, {
              onSuccess: (response) =>
                setResult(
                  response === null
                    ? 'Queued — the sync will run when the server is back'
                    : response.outcome === 'ok'
                      ? `${response.staged} new to review, ${response.eventsSeen} events read`
                      : response.outcome === 'not-modified'
                        ? 'Canvas has nothing new'
                        : // The banner above already carries the full message and
                          // what to do about it; repeating it here just makes the
                          // card long enough to push the queue off the screen.
                          response.outcome === 'invalid-feed'
                          ? 'Rejected — see above'
                          : (response.error ?? response.outcome),
                ),
              onError: (error) => setResult((error as Error).message),
            })
          }
        >
          Sync now
        </Action>
      </div>

      {result && <p className="tabular mt-2 text-[11.5px] text-[var(--text-muted)]">{result}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------

const SELECTORS: ReadonlyArray<{ selector: BulkSelector; label: string }> = [
  { selector: 'all-adds', label: 'Add all new' },
  { selector: 'safe-updates', label: 'Apply safe updates' },
]

/**
 * Bulk, in two taps.
 *
 * The first run produces fifteen proposals and one at a time is unusable at
 * that volume. But the confirmation step is not ceremony: it is where the
 * EXCLUSIONS are shown. A bulk action that quietly skipped an update touching a
 * field I authored would look identical to one that applied it, so what is
 * being left out is on screen before the button that runs it.
 */
function BulkBar({ onNote }: { onNote: (note: string) => void }) {
  const [open, setOpen] = useState<BulkSelector | null>(null)
  const status = useSyncStatus()
  const approve = useBulkApprove()
  const pending = status.data?.queue.pending ?? 0

  if (pending === 0) return null

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {SELECTORS.map(({ selector, label }) => (
          <BulkButton
            key={selector}
            selector={selector}
            label={label}
            active={open === selector}
            onClick={() => setOpen(open === selector ? null : selector)}
          />
        ))}
      </div>

      {open && (
        <BulkConfirm
          selector={open}
          busy={approve.isPending}
          onCancel={() => setOpen(null)}
          onConfirm={() => {
            approve.mutate(open, {
              onSuccess: (result) =>
                onNote(
                  result === null
                    ? 'Queued — will apply when the server is back'
                    : `Applied ${result.applied}, left ${result.excluded.length} for you`,
                ),
              onError: (error) => onNote((error as Error).message),
            })
            setOpen(null)
          }}
        />
      )}
    </div>
  )
}

function BulkButton({
  selector,
  label,
  active,
  onClick,
}: {
  selector: BulkSelector
  label: string
  active: boolean
  onClick: () => void
}) {
  const preview = useBulkPreview(selector, true)
  const count = preview.data?.preview.count ?? 0
  if (preview.data && count === 0) return null

  return (
    <button
      type="button"
      onClick={onClick}
      className={`tap tabular rounded-full border px-3 text-[11px] uppercase tracking-[0.06em] transition-colors ${
        active
          ? 'border-[var(--accent-line)] bg-[var(--accent-wash)] text-[var(--accent)]'
          : 'border-[var(--border)] bg-[var(--surface)] text-[var(--text-muted)]'
      }`}
    >
      {label}
      {preview.data && <span className="ml-1.5 text-[var(--text-subtle)]">{count}</span>}
    </button>
  )
}

function BulkConfirm({
  selector,
  busy,
  onConfirm,
  onCancel,
}: {
  selector: BulkSelector
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const preview = useBulkPreview(selector, true)
  if (!preview.data) return <div className="h-20 animate-pulse rounded-[var(--radius)] bg-[var(--surface-sunken)]" />

  const { count, describe, excluded } = preview.data.preview

  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--accent-line)] bg-[var(--surface)] p-3">
      <p className="text-[12.5px] leading-snug text-[var(--text)]">This will {describe}.</p>

      {excluded.length > 0 && (
        <div className="mt-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface-sunken)] p-2.5">
          <div className="tabular text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--text-subtle)]">
            Left for you · {excluded.length}
          </div>
          <ul className="mt-1 space-y-1">
            {excluded.map((item) => (
              <li key={item.proposalId} className="text-[11.5px] leading-snug text-[var(--text-muted)]">
                <span className="text-[var(--text)]">{item.title}</span> — {item.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-2.5 flex gap-1.5">
        <Action tone="primary" busy={busy} disabled={count === 0} onClick={onConfirm}>
          Do it
        </Action>
        <Action onClick={onCancel}>Cancel</Action>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------

function IgnoreList() {
  const ignored = useIgnored()
  const [open, setOpen] = useState(false)
  const entries = ignored.data?.ignored ?? []
  if (entries.length === 0) return null

  return (
    <div className="border-t border-[var(--rule)] pt-3">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="tap tabular text-[11px] uppercase tracking-[0.08em] text-[var(--text-subtle)]"
      >
        Ignored · {entries.length} {open ? '▾' : '▸'}
      </button>

      {open && (
        <>
          <p className="mt-1.5 text-[11.5px] leading-snug text-[var(--text-muted)]">
            These never come back to the queue, whatever Canvas does with them. Un-ignoring puts one back at the
            next sync.
          </p>
          <ul className="mt-2 space-y-1.5">
            {entries.map((entry) => (
              <IgnoredRow key={entry.externalId} title={entry.title} externalId={entry.externalId} reason={entry.reason} />
            ))}
          </ul>
        </>
      )}
    </div>
  )
}

function IgnoredRow({ title, externalId, reason }: { title: string; externalId: string; reason: string }) {
  const unignore = useUnignore()
  return (
    <li className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-2">
      <div className="min-w-0 flex-1">
        <p className="truncate text-[12.5px] text-[var(--text)]">{title || externalId}</p>
        {reason && <p className="tabular text-[11px] text-[var(--text-subtle)]">{reason}</p>}
      </div>
      <Action busy={unignore.isPending} onClick={() => unignore.mutate(externalId)}>
        Un-ignore
      </Action>
    </li>
  )
}

function Empty({ showSnoozed, skipped }: { showSnoozed: boolean; skipped: number }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-dashed border-[var(--border)] p-6 text-center">
      <p className="text-[13px] text-[var(--text-muted)]">
        {showSnoozed ? 'Nothing snoozed.' : 'Nothing to review.'}
      </p>
      {!showSnoozed && skipped > 0 && (
        <p className="tabular mt-1 text-[11px] text-[var(--text-subtle)]">{skipped} snoozed below</p>
      )}
    </div>
  )
}
